/* eslint-disable @typescript-eslint/no-unused-expressions */
import { useState } from 'react';
import { Form } from 'formik';
import { FormField } from '@gokrew/ui/form/FormField';
import { Eye, EyeOff } from 'lucide-react';
import {KollabHRLogo} from '@gokrew/ui/assets';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ResetPasswordForm } from '@gokrew/models/auth';
import { changePassword } from '@/api/auth';
import { resetTemporaryPasswordSchema } from '@gokrew/ui/validation/authSchema';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import { FormikWithSchema } from '@gokrew/ui/form';
import { useDialog } from '@gokrew/ui/context';
import type { DialogComponentProps } from '@gokrew/models/dialog';
import type { ApiResponse } from '@gokrew/models/generic';
import { FormikSubmitButton } from '@gokrew/ui/components/FormikSubmitButton';
import { PasswordValidationIndicator } from '@gokrew/ui/components/PasswordValidationIndicator';
import { QueryKeys } from '@/constants/query-keys';

interface InitialResetPasswordFormikValues extends ResetPasswordForm {
  confirmPassword: string;
}

interface InitialResetPasswordPageProps extends DialogComponentProps {
  dialogMode?: boolean;
}

export function InitialResetPasswordPage({
  dialogMode = false,
  dialogId,
}: InitialResetPasswordPageProps) {
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const queryClient = useQueryClient();
  const { t } = useTranslation(TranslationNS.InitialResetPassword);
  const { closeDialog } = useDialog();

  const initialValues: InitialResetPasswordFormikValues = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  };

  const resetMutation = useMutation<ApiResponse, Error, ResetPasswordForm>({
    mutationFn: (payload) => changePassword(payload),
    onSuccess: async () => {
      dialogId && closeDialog(dialogId);
      toast.success(t('toast.success'));
      queryClient.refetchQueries({ queryKey: [QueryKeys.Profile] });
    },
  });

  return (
    <div
      className={
        dialogMode
          ? 'p-1'
          : 'min-h-screen w-full flex items-center justify-center'
      }
    >
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-4">
          <div className="flex justify-center w-full">
            <img className="w-13 mb-2" src={KollabHRLogo} alt="Logo" />
          </div>

          <h2 className="text-2xl font-bold text-gray-900">{t('title')}</h2>

          <p className="text-gray-600 text-sm">{t('description')}</p>
        </div>

        <FormikWithSchema
          initialValues={initialValues}
          validationSchema={resetTemporaryPasswordSchema}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              const payload: ResetPasswordForm = {
                currentPassword: values.currentPassword,
                newPassword: values.newPassword,
              };

              await resetMutation.mutateAsync(payload);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ values }) => (
            <Form>
              {/* Temporary Password */}
              <FormField
                name="currentPassword"
                type={showNewPass ? 'text' : 'password'}
                label={t('form.label.current_password')}
                placeholder={t('form.placeholder.current_password')}
                IconSuffix={showNewPass ? Eye : EyeOff}
                onIconClick={() => setShowNewPass(!showNewPass)}
              />

              {/* New Password */}
              <FormField
                name="newPassword"
                type={showConfirmPass ? 'text' : 'password'}
                label={t('form.label.new_password')}
                placeholder={t('form.placeholder.new_password')}
                IconSuffix={showConfirmPass ? Eye : EyeOff}
                onIconClick={() => setShowConfirmPass(!showConfirmPass)}
              />

              {/* Confirm Password */}
              <FormField
                name="confirmPassword"
                type={showConfirmPass ? 'text' : 'password'}
                label={t('form.label.confirm_password')}
                placeholder={t('form.placeholder.confirm_password')}
                IconSuffix={showConfirmPass ? Eye : EyeOff}
                onIconClick={() => setShowConfirmPass(!showConfirmPass)}
              />

              <PasswordValidationIndicator
                password={values.newPassword}
                confirmPassword={values.confirmPassword}
                showMatchValidation={true}
                className="mb-2"
              />

              <FormikSubmitButton>{t('button.submit')}</FormikSubmitButton>
            </Form>
          )}
        </FormikWithSchema>
      </div>
    </div>
  );
}
